import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, buildingsApi } from '@/api';
import type { BuildingSummary } from '@/api/types';
import { useAuth } from './AuthContext';

const ACTIVE_BUILDING_KEY = 'omaraty.activeBuilding';

export type BuildingContextValue = {
  /** Buildings the signed-in principal may read. One entry for a manager. */
  buildings: BuildingSummary[];
  activeBuildingId: number | null;
  activeBuilding: BuildingSummary | null;
  /** Only admins may switch; for anyone else this is a no-op. */
  selectBuilding: (buildingId: number) => void;
  canSwitch: boolean;
  loading: boolean;
  error: string;
  reload: () => void;
};

const BuildingContext = createContext<BuildingContextValue | null>(null);

/**
 * Resolves which building the UI is currently looking at.
 *
 * The list always comes from the server (`buildingsApi.mine`), never from a
 * client-side guess, and the persisted selection is re-validated against that
 * list on every load. A stale or tampered id in localStorage is ignored.
 */
export function BuildingProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isAdmin, buildingId } = useAuth();

  const [buildings, setBuildings] = useState<BuildingSummary[]>([]);
  const [activeBuildingId, setActiveBuildingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!isAuthenticated) {
      setBuildings([]);
      setActiveBuildingId(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    buildingsApi
      .mine()
      .then((rows) => {
        if (cancelled) return;
        setBuildings(rows);
        setError('');

        const allowed = new Set(rows.map((row) => row.id));

        // A manager or resident is pinned to their own building, full stop.
        if (!isAdmin) {
          setActiveBuildingId(buildingId !== null && allowed.has(buildingId) ? buildingId : null);
          return;
        }

        const storedRaw = localStorage.getItem(ACTIVE_BUILDING_KEY);
        const stored = storedRaw !== null && /^\d{1,9}$/.test(storedRaw) ? Number(storedRaw) : null;

        setActiveBuildingId(
          stored !== null && allowed.has(stored) ? stored : (rows[0]?.id ?? null),
        );
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // Keys, not sentences: the consuming page translates them.
        setError(cause instanceof ApiError ? 'errors.forbidden' : 'errors.unexpected');
        setBuildings([]);
        setActiveBuildingId(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isAdmin, buildingId, nonce]);

  const selectBuilding = useCallback(
    (nextId: number) => {
      if (!isAdmin) return;
      if (!buildings.some((building) => building.id === nextId)) return;
      localStorage.setItem(ACTIVE_BUILDING_KEY, String(nextId));
      setActiveBuildingId(nextId);
    },
    [isAdmin, buildings],
  );

  const value = useMemo<BuildingContextValue>(
    () => ({
      buildings,
      activeBuildingId,
      activeBuilding: buildings.find((b) => b.id === activeBuildingId) ?? null,
      selectBuilding,
      canSwitch: isAdmin && buildings.length > 1,
      loading,
      error,
      reload,
    }),
    [buildings, activeBuildingId, selectBuilding, isAdmin, loading, error, reload],
  );

  return <BuildingContext.Provider value={value}>{children}</BuildingContext.Provider>;
}

export function useBuilding(): BuildingContextValue {
  const context = useContext(BuildingContext);
  if (!context) throw new Error('useBuilding must be used inside BuildingProvider');
  return context;
}

/**
 * For pages that cannot render without a tenant. Returns null while resolving,
 * which callers treat as a loading state.
 */
export function useActiveBuildingId(): number | null {
  return useBuilding().activeBuildingId;
}
